import * as THREE from 'three'

import EventEmitter from '../Utils/EventEmitter.js'

import ScanModel from '../ScanModel.js'
import { TweenLite, Power2 } from 'gsap'

export default class Simulation extends EventEmitter
{
    constructor(_options = {})
    {
        super()

        // Set up
        this.$eventHub = _options.$eventHub
        this.mobile = _options.mobile
        this.camera = _options.camera
        this.sizes = _options.sizes
        this.time = _options.time
        this.cursor = _options.cursor
        this.ui = _options.ui
        this.loader = _options.loader

        // Container
        this.container = new THREE.Object3D()

        // Camera container
        this.viewContainer = new THREE.Object3D()
        this.container.add(this.viewContainer)

        this.setRunning()
        this.setScene()
        this.setInteractions()
        this.setPillar()
        this.setTestPoints()
        this.setBeam()
        this.setDangerAreas()
    }

    /**
     * Set running
     */
    setRunning()
    {
        this.running = false

        this.$eventHub.$on('experience.simulation.start', () =>
        {
            this.running = true
        })

        this.$eventHub.$on('experience.simulation.stop', () =>
        {
            this.running = false
        })
    }

    /**
     * Set interactions
     */
    setInteractions()
    {
        // Set up
        this.interactions = {}

        this.interactions.target = { x: 0, y: - 0.76, z: 0 }
        this.interactions.won = false

        /**
         * Directions
         */
        this.interactions.directions = {}
        this.interactions.directions.zPos = {}
        this.interactions.directions.zPos.active = false
        this.interactions.directions.zPos.keyCodes = [38, 90, 87, 56]
        this.interactions.directions.zNeg = {}
        this.interactions.directions.zNeg.active = false
        this.interactions.directions.zNeg.keyCodes = [40, 83, 50]
        this.interactions.directions.xPos = {}
        this.interactions.directions.xPos.active = false
        this.interactions.directions.xPos.keyCodes = [37, 81, 52]
        this.interactions.directions.xNeg = {}
        this.interactions.directions.xNeg.active = false
        this.interactions.directions.xNeg.keyCodes = [39, 68, 54]
        this.interactions.directions.yPos = {}
        this.interactions.directions.yPos.active = false
        this.interactions.directions.yPos.keyCodes = [16, 55, 57, 69]
        this.interactions.directions.yNeg = {}
        this.interactions.directions.yNeg.active = false
        this.interactions.directions.yNeg.keyCodes = [32, 49, 51, 65]

        const controlsMapping = {
            up: 'zPos',
            down: 'zNeg',
            left: 'xPos',
            right: 'xNeg',
            shift: 'yPos',
            space: 'yNeg'
        }
        this.$eventHub.$on('experience.simulation.uiButtonDown', (_buttonName) =>
        {
            const directionName = controlsMapping[_buttonName]
            const direction = this.interactions.directions[directionName]

            this.$eventHub.$emit('experience.simulation.buttonDown', directionName)

            direction.active = true
        })
        this.$eventHub.$on('experience.simulation.uiButtonUp', (_buttonName) =>
        {
            const directionName = controlsMapping[_buttonName]
            const direction = this.interactions.directions[directionName]

            this.$eventHub.$emit('experience.simulation.buttonUp', directionName)

            direction.active = false
        })

        window.addEventListener('keydown', (_event) =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            for(const _directionName in this.interactions.directions)
            {
                const direction = this.interactions.directions[_directionName]
                if(direction.keyCodes.indexOf(_event.keyCode) !== -1)
                {
                    _event.preventDefault()

                    this.$eventHub.$emit('experience.simulation.buttonDown', _directionName)

                    direction.active = true
                }
            }
        })

        window.addEventListener('keyup', (_event) =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            for(const _directionName in this.interactions.directions)
            {
                const direction = this.interactions.directions[_directionName]
                if(direction.keyCodes.indexOf(_event.keyCode) !== -1)
                {
                    _event.preventDefault()

                    this.$eventHub.$emit('experience.simulation.buttonUp', _directionName)

                    direction.active = false
                }
            }
        })

        /**
         * Speed
         */
        this.interactions.speed = {}
        this.interactions.speed.acceleration = 0.000005
        this.interactions.speed.maxSpeed = 0.005
        this.interactions.speed.dumping = 0.9
        this.interactions.speed.chockDumping = 0.5
        this.interactions.speed.x = 0
        this.interactions.speed.y = 0
        this.interactions.speed.z = 0

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Already won
            if(this.interactions.won)
            {
                return
            }

            // X axis
            if(this.interactions.directions.xPos.active)
            {
                this.interactions.speed.x += this.interactions.speed.acceleration * this.time.delta
            }
            if(this.interactions.directions.xNeg.active)
            {
                this.interactions.speed.x -= this.interactions.speed.acceleration * this.time.delta
            }
            if(Math.abs(this.interactions.speed.x) > this.interactions.speed.maxSpeed)
            {
                this.interactions.speed.x = this.interactions.speed.maxSpeed * Math.sign(this.interactions.speed.x)
            }

            if(!this.interactions.directions.xPos.active && !this.interactions.directions.xNeg.active)
            {
                this.interactions.speed.x *= this.interactions.speed.dumping
            }

            // Y axis
            if(this.interactions.directions.yPos.active)
            {
                this.interactions.speed.y += this.interactions.speed.acceleration * this.time.delta
            }
            if(this.interactions.directions.yNeg.active)
            {
                this.interactions.speed.y -= this.interactions.speed.acceleration * this.time.delta
            }
            if(Math.abs(this.interactions.speed.y) > this.interactions.speed.maxSpeed)
            {
                this.interactions.speed.y = this.interactions.speed.maxSpeed * Math.sign(this.interactions.speed.y)
            }

            if(!this.interactions.directions.yPos.active && !this.interactions.directions.yNeg.active)
            {
                this.interactions.speed.y *= this.interactions.speed.dumping
            }

            // Z axis
            if(this.interactions.directions.zPos.active)
            {
                this.interactions.speed.z += this.interactions.speed.acceleration * this.time.delta
            }
            if(this.interactions.directions.zNeg.active)
            {
                this.interactions.speed.z -= this.interactions.speed.acceleration * this.time.delta
            }
            if(Math.abs(this.interactions.speed.z) > this.interactions.speed.maxSpeed)
            {
                this.interactions.speed.z = this.interactions.speed.maxSpeed * Math.sign(this.interactions.speed.z)
            }

            if(!this.interactions.directions.zPos.active && !this.interactions.directions.zNeg.active)
            {
                this.interactions.speed.z *= this.interactions.speed.dumping
            }
        })

        const arePointsInArea = (_points) =>
        {
            const points = _points instanceof Array ? _points : [_points]

            for(const _area of this.dangerAreas.coordinates)
            {
                for(const _point of points)
                {
                    if(
                        _point.x > _area[0] - _area[3] * 0.5 && _point.x < _area[0] + _area[3] * 0.5 &&
                        _point.y > _area[1] - _area[4] * 0.5 && _point.y < _area[1] + _area[4] * 0.5 &&
                        _point.z > _area[2] - _area[5] * 0.5 && _point.z < _area[2] + _area[5] * 0.5
                    )
                    {
                        return true
                    }
                }
            }

            return false
        }

        /**
         * Update
         */
        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Already won
            if(this.interactions.won)
            {
                return
            }

            const x = this.pillar.container.position.x + this.interactions.speed.x
            const y = this.pillar.container.position.y + this.interactions.speed.y
            const z = this.pillar.container.position.z + this.interactions.speed.z

            let choked = false

            // Going right and in area
            if(x < this.pillar.container.position.x && arePointsInArea(this.testPoints.coordinates.xNeg.current))
            {
                this.interactions.speed.x *= - 1
                choked = true
            }

            // Going left and in area
            if(x > this.pillar.container.position.x && arePointsInArea(this.testPoints.coordinates.xPos.current))
            {
                this.interactions.speed.x *= - 1
                choked = true
            }

            // Going right and in area
            if(y < this.pillar.container.position.y && arePointsInArea([this.testPoints.coordinates.xNeg.current, this.testPoints.coordinates.xPos.current, this.testPoints.coordinates.zNeg.current, this.testPoints.coordinates.zPos.current]))
            {
                this.interactions.speed.y *= - 1
                choked = true
            }

            // Going left and in area
            if(y > this.pillar.container.position.y && arePointsInArea([this.testPoints.coordinates.xNeg.current, this.testPoints.coordinates.xPos.current, this.testPoints.coordinates.zNeg.current, this.testPoints.coordinates.zPos.current]))
            {
                this.interactions.speed.y *= - 1
                choked = true
            }

            // Going up and in area
            if(z < this.pillar.container.position.z && arePointsInArea(this.testPoints.coordinates.zNeg.current))
            {
                this.interactions.speed.z *= - 1
                choked = true
            }

            // Going down and in area
            if(z > this.pillar.container.position.z && arePointsInArea(this.testPoints.coordinates.zPos.current))
            {
                this.interactions.speed.z *= - 1
                choked = true
            }

            this.pillar.container.position.x += this.interactions.speed.x
            this.pillar.container.position.y += this.interactions.speed.y
            this.pillar.container.position.z += this.interactions.speed.z

            // Distance and speed
            const distance = Math.hypot(this.interactions.target.x - this.pillar.container.position.x, this.interactions.target.y - this.pillar.container.position.y, this.interactions.target.z - this.pillar.container.position.z)
            const speed = Math.hypot(this.interactions.speed.x, this.interactions.speed.y, this.interactions.speed.z)

            if(choked)
            {
                this.interactions.speed.x *= 0.5
                this.interactions.speed.y *= 0.5
                this.interactions.speed.z *= 0.5

                if(speed > 0.002)
                {
                    this.$eventHub.$emit('experience.simulation.choked')

                    TweenLite.killTweensOf(this.scene.object.material.uniforms.uColor.value)
                    TweenLite.fromTo(this.scene.object.material.uniforms.uColor.value, 2, { x: 129 / 255 / 2, y: 53 / 255 / 2, z: 53 / 255 / 2 }, { x: 0.12, y: 0.12, z: 0.12 })
                }
            }

            // View
            const targetY = - this.pillar.container.position.y * 0.35
            const targetX = - this.pillar.container.position.x * 0.35
            this.viewContainer.position.y += (targetY - this.viewContainer.position.y) * 0.016
            this.viewContainer.position.x += (targetX - this.viewContainer.position.x) * 0.016

            // Target
            if(distance < 0.035 && speed < 0.00004)
            {
                this.interactions.won = true

                this.pillar.object.material.uniforms.uColor.value.set(0.3, 0.6, 0)
                this.scene.target.material.uniforms.uColor.value.set(0.3, 0.6, 0)
                TweenLite.to(this.pillar.structure.container.position, 2, { y: 1.25, ease: Power2.easeInOut })
                TweenLite.to(this.pillar.structure.material.uniforms.uAlpha, 1, { value: 0, delay: 1, onComplete: () => { this.pillar.structure.material.visible = false } })
                TweenLite.to(this.beam.circle1.material, 1, { opacity: 0, delay: 1 })
                TweenLite.to(this.beam.circle2.material, 1, { opacity: 0, delay: 1 })
                TweenLite.to(this.beam.circle3.material, 1, { opacity: 0, delay: 1 })
                TweenLite.to(this.scene.object.material.uniforms.uColor.value, 2, { x: 0.2, y: 0.2, z: 0.2, delay: 1.5 })

                this.$eventHub.$emit('experience.simulation.won')
            }
        })
    }

    /**
     * Set scene
     */
    setScene()
    {
        this.scene = {}

        // Container
        this.scene.container = new THREE.Object3D()
        this.scene.container.scale.x = 0.125
        this.scene.container.scale.y = 0.125
        this.scene.container.scale.z = 0.125
        this.scene.container.rotation.y = Math.PI
        this.viewContainer.add(this.scene.container)

        // Object
        this.scene.object = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulationSceneModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 0.12, y: 0.12, z: 0.12 } // Gray
        })
        this.scene.container.add(this.scene.object.container)

        // Target
        this.scene.target = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulationTargetModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.scene.container.add(this.scene.target.container)

        // Area
        this.scene.target = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulationAreaModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.scene.container.add(this.scene.target.container)
    }

    /**
     * Set pillar
     */
    setPillar()
    {
        this.pillar = {}

        // Container
        this.pillar.container = new THREE.Object3D()
        this.pillar.container.scale.x = 0.125
        this.pillar.container.scale.y = 0.125
        this.pillar.container.scale.z = 0.125
        this.pillar.container.position.y = 0.15
        this.pillar.container.position.x = 0.4
        this.pillar.container.position.z = - 0.2
        // this.pillar.container.position.y = - 0.5
        // this.pillar.container.position.x = 0
        // this.pillar.container.position.z = 0
        this.viewContainer.add(this.pillar.container)

        // Object
        this.pillar.object = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulationPillarModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 0.4, y: 0.4, z: 0.4 } // Gray
        })
        this.pillar.container.add(this.pillar.object.container)

        // Structure
        this.pillar.structure = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulationStructureModelObj.geometry,
            revealPosition: { x: 0, y: 13, z: 0 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.pillar.structure.material.uniforms.uRevealDistance.value = 2
        this.pillar.container.add(this.pillar.structure.container)
    }

    /**
     * Set test points
     */
    setTestPoints()
    {
        // Test points
        this.testPoints = {}
        this.testPoints.debug = false

        this.testPoints.coordinates = {
            xPos:
            {
                origin: { x: 0.125, y: 0.02, z: 0 },
                current: { x: 0.125, y: 0.02, z: 0 }
            },
            xNeg:
            {
                origin: { x: -0.125, y: 0.02, z: 0 },
                current: { x: -0.125, y: 0.02, z: 0 }
            },
            zPos:
            {
                origin: { x: 0, y: 0.02, z: 0.125 },
                current: { x: 0, y: 0.02, z: 0.125 }
            },
            zNeg:
            {
                origin: { x: 0, y: 0.02, z: -0.125 },
                current: { x: 0, y: 0.02, z: -0.125 }
            }
        }

        this.testPoints.container = new THREE.Object3D()
        this.viewContainer.add(this.testPoints.container)

        if(this.testPoints.debug)
        {
            const geometry = new THREE.BoxBufferGeometry(0.02, 0.02, 0.02)
            const material = new THREE.MeshBasicMaterial({ color: 0x00ffff, wireframe: true })

            for(const _coordinatesName in this.testPoints.coordinates)
            {
                const _coordinates = this.testPoints.coordinates[_coordinatesName]
                const mesh = new THREE.Mesh(geometry, material)
                mesh.position.set(_coordinates.origin.x, _coordinates.origin.y, _coordinates.origin.z)
                this.testPoints.container.add(mesh)
            }
        }

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Already won
            if(this.interactions.won)
            {
                return
            }

            this.testPoints.container.position.copy(this.pillar.container.position)

            for(const _coordinatesName in this.testPoints.coordinates)
            {
                const _coordinates = this.testPoints.coordinates[_coordinatesName]
                _coordinates.current.x = _coordinates.origin.x + this.testPoints.container.position.x
                _coordinates.current.y = _coordinates.origin.y + this.testPoints.container.position.y
                _coordinates.current.z = _coordinates.origin.z + this.testPoints.container.position.z
            }
        })
    }

    /**
     * Set beam
     */
    setBeam()
    {
        this.beam = {}

        // Container
        this.beam.container = new THREE.Object3D()
        this.viewContainer.add(this.beam.container)

        // Circle
        this.beam.circle1 = {}
        this.beam.circle1.geometry = new THREE.RingBufferGeometry(0.02, 0.024, 6, 1)
        this.beam.circle1.material = new THREE.MeshBasicMaterial({ color: 0xffe600, blending: THREE.AdditiveBlending, transparent: true })
        this.beam.circle1.mesh = new THREE.Mesh(this.beam.circle1.geometry, this.beam.circle1.material)
        this.beam.circle1.mesh.rotation.x = - Math.PI * 0.5
        this.beam.container.add(this.beam.circle1.mesh)

        this.beam.circle2 = {}
        this.beam.circle2.geometry = new THREE.RingBufferGeometry(0.1, 0.1025, 12, 1)
        this.beam.circle2.material = new THREE.MeshBasicMaterial({ color: 0xaaaaaa, blending: THREE.AdditiveBlending, transparent: true })
        this.beam.circle2.mesh = new THREE.Mesh(this.beam.circle2.geometry, this.beam.circle2.material)
        this.beam.circle2.mesh.rotation.x = - Math.PI * 0.5
        this.beam.container.add(this.beam.circle2.mesh)

        this.beam.circle3 = {}
        this.beam.circle3.geometry = new THREE.RingBufferGeometry(0.12, 0.122, 12, 1)
        this.beam.circle3.material = new THREE.MeshBasicMaterial({ color: 0x333333, blending: THREE.AdditiveBlending, transparent: true })
        this.beam.circle3.mesh = new THREE.Mesh(this.beam.circle3.geometry, this.beam.circle3.material)
        this.beam.circle3.mesh.rotation.x = - Math.PI * 0.5
        this.beam.container.add(this.beam.circle3.mesh)

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            this.beam.container.position.copy(this.pillar.container.position)

            // Find distance
            let minDistance = Infinity

            for(const _pointName in this.testPoints.coordinates)
            {
                const _point = this.testPoints.coordinates[_pointName].current

                for(const _area of this.dangerAreas.coordinates)
                {
                    if(
                        _point.x > _area[0] - _area[3] * 0.5 && _point.x < _area[0] + _area[3] * 0.5 &&
                        _point.z > _area[2] - _area[5] * 0.5 && _point.z < _area[2] + _area[5] * 0.5 &&
                        _point.y > _area[1] + _area[4] * 0.5
                    )
                    {
                        const distance = _point.y - (_area[1] + _area[4] * 0.5)
                        if(distance < minDistance)
                        {
                            minDistance = distance
                        }
                    }
                }
            }

            if(minDistance > 0)
            {
                this.beam.circle1.mesh.position.y = - minDistance
                this.beam.circle2.mesh.position.y = - minDistance
                this.beam.circle3.mesh.position.y = - minDistance
            }
        })
    }

    /**
     * Set danger areas
     */
    setDangerAreas()
    {
        this.dangerAreas = {}
        this.dangerAreas.debug = false

        // Container
        this.dangerAreas.container = new THREE.Object3D()
        this.viewContainer.add(this.dangerAreas.container)

        // Material
        this.dangerAreas.material = new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true })

        // Options
        this.dangerAreas.coordinates = [
            // Floor center
            [
                0, - 0.795, 0.08,
                0.42, 0.1, 0.82
            ],
            // Floor left
            [
                0.43, - 0.45, 0.08,
                0.44, 0.9, 0.82
            ],
            // Floor right
            [
                - 0.43, - 0.45, 0.08,
                0.44, 0.9, 0.82
            ],
            // Floor up
            [
                0, - 0.45, 0.56,
                1.3, 0.9, 0.14
            ],
            // Floor down
            [
                0, - 0.45, - 0.74,
                1.3, 0.9, 0.82
            ],
            // Wall up
            [
                0, 0.2, 0.68,
                1.3, 0.4, 0.1
            ],
            // Wall down
            [
                0, 0.2, - 1.2,
                1.3, 0.4, 0.1
            ],
            // Wall left
            [
                0.60, 0.2, - 0.26,
                0.1, 0.4, 1.78
            ],
            // Wall right
            [
                - 0.60, 0.2, - 0.26,
                0.1, 0.4, 1.78
            ],
            // Roof
            [
                0, 0.45, - 0.26,
                1.3, 0.1, 1.98
            ]
        ]

        if(this.dangerAreas.debug)
        {
            // Each danger Area
            for(const _coordinates of this.dangerAreas.coordinates)
            {
                const geometry = new THREE.BoxBufferGeometry(_coordinates[3], _coordinates[4], _coordinates[5])
                const mesh = new THREE.Mesh(geometry, this.dangerAreas.material)

                mesh.position.set(_coordinates[0], _coordinates[1], _coordinates[2])

                this.dangerAreas.container.add(mesh)
            }
        }
    }

    /**
     * Reset
     */
    reset()
    {
        this.pillar.container.position.y = 0.15
        this.pillar.container.position.x = 0.4
        this.pillar.container.position.z = 0.3

        this.interactions.won = false

        this.pillar.object.material.uniforms.uColor.value.set(0.4, 0.4, 0.4)
        this.scene.target.material.uniforms.uColor.value.set(1, 0.9, 0)
        this.pillar.structure.container.position.y = 0
        this.pillar.structure.material.uniforms.uAlpha.value = 1
        this.pillar.structure.material.visible = true
        this.beam.circle1.material.opacity = 1
        this.beam.circle2.material.opacity = 1
        this.beam.circle3.material.opacity = 1
        this.scene.object.material.uniforms.uColor.value.set(0.12, 0.12, 0.12)
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Interactions/Simulation.js